import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2,
  Ticket,
  ScanLine,
  Bell,
  Save,
  ShieldCheck,
  Info,
  CreditCard,
  KeyRound,
} from "lucide-react";

import AdminPageHeader from "@/components/layout/AdminPageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SCREENS } from "@/constants/testIds";
import {
  leerConfig,
  guardarConfig,
  hidratarConfig,
} from "@/data/adminConfigStore";

function Section({ icon: Icon, titulo, descripcion, children }) {
  return (
    <section className="rounded-2xl border hairline bg-card p-6 md:p-7 flex flex-col gap-5">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent ring-1 ring-accent/30 grid place-items-center shrink-0">
          <Icon className="w-4 h-4" strokeWidth={1.7} />
        </div>
        <div>
          <h3 className="font-heading text-lg leading-tight">{titulo}</h3>
          {descripcion && (
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {descripcion}
            </p>
          )}
        </div>
      </header>
      {children}
    </section>
  );
}

/**
 * Configuración general · Auditoría 055-079.
 *
 * - Smart Queue: 10 minutos (nuevo default).
 * - Inactividad: 3 minutos (nuevo default).
 * - El límite global de boletos por usuario NO se configura aquí; se
 *   define por evento en el Paso 2 del Wizard.
 * - Recepción: búsqueda por nombre / número de socio / validación
 *   manual quedan SIEMPRE habilitadas. Se muestran como reglas de
 *   negocio institucionales, sin toggles.
 * - Notificaciones: el sistema elige automáticamente el canal
 *   adecuado (correo / push). No hay toggles manuales.
 */
export default function AdminConfiguracion() {
  const [cfg, setCfg] = useState(() => leerConfig());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    hidratarConfig().then((fresh) => {
      if (fresh) setCfg(fresh);
    });
  }, []);

  const setPath = (grupo, campo) => (val) => {
    setCfg((c) => ({ ...c, [grupo]: { ...c[grupo], [campo]: val } }));
  };
  const setStr = (g, c) => (e) => setPath(g, c)(e.target.value);
  const setNum = (g, c) => (e) =>
    setPath(g, c)(e.target.value === "" ? "" : Number(e.target.value));

  const guardar = async () => {
    setSaving(true);
    const res = await guardarConfig(cfg);
    setSaving(false);
    if (!res || res.error) {
      const err = (res && res.error) || "";
      const msg =
        typeof err === "string" && err.trim() && err !== "network"
          ? err
          : err === "network"
            ? "Sin conexión con el servidor. Intenta más tarde."
            : "No se pudo guardar la configuración.";
      toast.error(msg);
      return;
    }
    setCfg(res);
    toast.success("Configuración guardada.");
  };

  return (
    <div
      data-testid={SCREENS.adminConfiguracion}
      className="flex flex-col gap-8"
    >
      <AdminPageHeader
        eyebrow="Portal administrativo"
        title="Configuración general"
        description="Parámetros institucionales y operativos del portal."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section icon={Building2} titulo="Institución">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nombre</Label>
              <Input
                data-testid="cfg-institucion-nombre"
                value={cfg.institucion.nombre}
                onChange={setStr("institucion", "nombre")}
              />
            </div>
            <div className="col-span-2">
              <Label>Logo (URL)</Label>
              <Input
                data-testid="cfg-institucion-logo"
                value={cfg.institucion.logo}
                onChange={setStr("institucion", "logo")}
                placeholder="https://…"
              />
            </div>
            <div>
              <Label>Correo institucional</Label>
              <Input
                type="email"
                data-testid="cfg-institucion-correo"
                value={cfg.institucion.correoInstitucional}
                onChange={setStr("institucion", "correoInstitucional")}
              />
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input
                data-testid="cfg-institucion-telefono"
                value={cfg.institucion.telefono}
                onChange={setStr("institucion", "telefono")}
              />
            </div>
          </div>
        </Section>

        <Section
          icon={Ticket}
          titulo="Venta"
          descripcion="El límite de boletos por usuario se configura por evento (Paso 2 del Wizard)."
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tiempo máx. Smart Queue (min)</Label>
              <Input
                type="number"
                min={1}
                data-testid="cfg-venta-smartqueue"
                value={cfg.venta.smartQueueMaxMinutos}
                onChange={setNum("venta", "smartQueueMaxMinutos")}
              />
            </div>
            <div>
              <Label>Inactividad (min)</Label>
              <Input
                type="number"
                min={1}
                data-testid="cfg-venta-inactividad"
                value={cfg.venta.inactividadMinutos}
                onChange={setNum("venta", "inactividadMinutos")}
              />
            </div>
          </div>
        </Section>

        <Section
          icon={ScanLine}
          titulo="Recepción"
          descripcion="Reglas institucionales de acceso · siempre habilitadas."
        >
          <ul className="flex flex-col gap-2">
            {[
              "Búsqueda por nombre",
              "Búsqueda por número de socio",
              "Validación manual",
            ].map((label) => (
              <li
                key={label}
                data-testid={`cfg-recepcion-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center gap-3 rounded-xl border hairline bg-emerald-500/5 border-emerald-500/30 px-4 py-3"
              >
                <ShieldCheck
                  className="w-4 h-4 text-emerald-700 dark:text-emerald-300 shrink-0"
                  strokeWidth={1.8}
                />
                <span className="text-sm flex-1">{label}</span>
                <span className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                  Siempre activo
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section
          icon={Bell}
          titulo="Notificaciones"
          descripcion="El sistema decide automáticamente el canal más adecuado."
        >
          <div
            data-testid="cfg-notif-auto"
            className="rounded-xl border hairline bg-muted/30 px-4 py-3 flex items-start gap-3 text-sm"
          >
            <Info className="w-4 h-4 text-accent mt-0.5" strokeWidth={1.8} />
            <span className="leading-relaxed">
              El canal (correo o push) se elige automáticamente según el tipo
              de notificación y la disponibilidad del socio. No requiere
              configuración manual.
            </span>
          </div>
        </Section>

        <Section
          icon={CreditCard}
          titulo="Pasarela BanBajío"
          descripcion="Credenciales · sandbox y producción. Los secretos se enmascaran al guardar; sólo se sobrescriben si capturas un valor nuevo."
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Modo activo</Label>
              <select
                data-testid="cfg-pagos-modo"
                value={cfg.pagos?.modo || "sandbox"}
                onChange={(e) => setPath("pagos", "modo")(e.target.value)}
                className="w-full rounded-md border hairline bg-background px-3 py-2 text-sm mt-1"
              >
                <option value="sandbox">Sandbox (pruebas)</option>
                <option value="produccion">Producción</option>
              </select>
            </div>
            <div>
              <Label>Merchant ID</Label>
              <Input
                data-testid="cfg-pagos-merchant"
                value={cfg.pagos?.merchantId || ""}
                onChange={setStr("pagos", "merchantId")}
                placeholder="MID-…"
              />
            </div>
            <div>
              <Label>API Key</Label>
              <div className="relative">
                <KeyRound className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.7} />
                <Input
                  data-testid="cfg-pagos-apikey"
                  value={cfg.pagos?.apiKey || ""}
                  onChange={setStr("pagos", "apiKey")}
                  placeholder="•••••••"
                  className="pl-7"
                />
              </div>
            </div>
            <div className="col-span-2">
              <Label>Secret Key (webhooks)</Label>
              <div className="relative">
                <KeyRound className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.7} />
                <Input
                  data-testid="cfg-pagos-secretkey"
                  value={cfg.pagos?.secretKey || ""}
                  onChange={setStr("pagos", "secretKey")}
                  placeholder="•••••••"
                  className="pl-7"
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                HMAC-SHA256 usado por el backend para verificar la firma
                de los webhooks entrantes de BanBajío.
              </p>
            </div>
            <div className="col-span-2">
              <Label>Endpoint Sandbox</Label>
              <Input
                data-testid="cfg-pagos-endpoint-sandbox"
                value={cfg.pagos?.endpointSandbox || ""}
                onChange={setStr("pagos", "endpointSandbox")}
                placeholder="https://sandbox.banbajio.com/…"
              />
            </div>
            <div className="col-span-2">
              <Label>Endpoint Producción</Label>
              <Input
                data-testid="cfg-pagos-endpoint-prod"
                value={cfg.pagos?.endpointProduccion || ""}
                onChange={setStr("pagos", "endpointProduccion")}
                placeholder="https://api.banbajio.com/…"
              />
            </div>
          </div>
          <div
            data-testid="cfg-pagos-info"
            className="rounded-xl border hairline bg-emerald-500/5 border-emerald-500/30 px-4 py-3 flex items-start gap-3 text-xs mt-2"
          >
            <ShieldCheck className="w-4 h-4 text-emerald-700 dark:text-emerald-300 mt-0.5" strokeWidth={1.8} />
            <span className="leading-relaxed">
              Al capturar los 5 campos y guardar, el sistema conmuta
              automáticamente al proveedor real. Mientras algún campo esté
              vacío se sigue operando con el simulador local (MOCK).
            </span>
          </div>
        </Section>
      </div>

      <div className="flex justify-end">
        <Button
          data-testid="cfg-guardar"
          onClick={guardar}
          disabled={saving}
          className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Guardando…" : "Guardar configuración"}
        </Button>
      </div>
    </div>
  );
}
